import classNames from 'classnames';
import { type RefObject, useContext, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import usePlatform from '@/hooks/use-platform';
import { layoutClassNames } from '@/utils/consts';

import SplitLayout from './SplitLayout';
import useStepTransition from './use-step-transition';

/**
 * Moves focus to the new step's heading, since the control that navigated has unmounted and
 * focus would otherwise fall back to <body>. Skips the first load so autofocus wins there.
 */
// eslint-disable-next-line @typescript-eslint/ban-types -- React 19's useRef(null) yields RefObject<T | null>
const useStepFocus = (containerRef: RefObject<HTMLElement | null>) => {
  const { key } = useLocation();
  const previousKey = useRef(key);

  useEffect(() => {
    if (previousKey.current === key) {
      return;
    }

    // eslint-disable-next-line @silverhand/fp/no-mutation
    previousKey.current = key;

    const focusHeading = () => {
      const container = containerRef.current;
      const heading = container?.querySelector('h1');

      if (!container || !heading) {
        return false;
      }

      // A field the new step autofocused keeps focus.
      if (!container.contains(document.activeElement)) {
        heading.focus({ preventScroll: true });
      }

      return true;
    };

    // eslint-disable-next-line @silverhand/fp/no-let -- reassigned when a lazy page is still loading
    let observer: MutationObserver | undefined;
    const frame = requestAnimationFrame(() => {
      const container = containerRef.current;

      if (focusHeading() || !container || typeof MutationObserver === 'undefined') {
        return;
      }

      // eslint-disable-next-line @silverhand/fp/no-mutation
      observer = new MutationObserver(() => {
        if (focusHeading()) {
          observer?.disconnect();
        }
      });
      observer.observe(container, { childList: true, subtree: true });
    });

    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [containerRef, key]);
};

const AppLayout = () => {
  const { isMobile } = usePlatform();
  const { experienceSettings, isPreview, loading } = useContext(PageContext);
  // The dev-tenant banner is fixed to the top; on mobile nothing else would clear it.
  const hasDevBanner = Boolean(experienceSettings?.isDevelopmentTenant) && !isPreview;
  const containerRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useStepTransition(contentRef);
  useStepFocus(containerRef);

  const content = (
    <div ref={contentRef} aria-busy={loading} className="flex w-full flex-1 flex-col">
      <Outlet />
    </div>
  );

  if (!isMobile) {
    return <SplitLayout mainRef={containerRef}>{content}</SplitLayout>;
  }

  // Mobile scrolls the document itself: inside a full-screen scroller, iOS cannot bring a focused
  // field above the keyboard and pans the whole page instead, pushing the fields out of view.
  return (
    <div className="min-h-dvh bg-background">
      <div
        className={classNames(
          'flex min-h-dvh flex-col items-center',
          layoutClassNames.pageContainer
        )}
      >
        <main
          ref={containerRef}
          className={classNames(
            'flex w-full flex-1 flex-col',
            hasDevBanner
              ? 'pt-[calc(max(0.375rem,env(safe-area-inset-top))+2.5rem)]'
              : 'pt-[max(1rem,env(safe-area-inset-top))]',
            'pr-[max(1.25rem,env(safe-area-inset-right))] pb-[max(1.5rem,env(safe-area-inset-bottom))] pl-[max(1.25rem,env(safe-area-inset-left))]',
            layoutClassNames.mainContent
          )}
        >
          {content}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
